import React, { useCallback, useEffect, useState } from "react";
import { IoTrashOutline } from "react-icons/io5";
import ConnectSourceCatalogIcon from "./ConnectSourceCatalogIcon";
import { InlineSpinner } from "./SourceMessageAccordion";
import CrmWriteActivityPanel from "./CrmWriteActivityPanel";
import {
  disconnectCrmConnector,
  getCrmConnectorActivity,
  getCrmConnectorStatus,
} from "../../businessModeService";
import {
  getCrmDetailHeaderMeta,
  normalizeCrmConnectorStatus,
} from "./crmConnectorStatus";
import { clearGateChoice } from "./connectorRunGate";

/**
 * Detail pane for a connected CRM write-back connector (Attio, Salesforce).
 * Layout mirrors digest connectors (Granola, Google Meet): header + Activity only.
 *
 * @example
 * <CrmConnectorDetailPanel
 *   app={{ id: "attio", appName: "Attio" }}
 *   status={{ connected: true, writes_count: 2, last_written_at: "2026-07-24T10:30:00Z" }}
 *   onStatusChange={(crmId, next) => setCrmStatusById((prev) => ({ ...prev, attio: next }))}
 * />
 */
const CrmConnectorDetailPanel = ({
  app,
  status,
  onStatusChange,
}) => {
  const crmId = String(app?.id || "").trim();
  const providerLabel = String(app?.appName || "CRM").trim();
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState("");
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const normalizedStatus = normalizeCrmConnectorStatus(status);
  const headerMeta = getCrmDetailHeaderMeta(normalizedStatus);

  const loadActivity = useCallback(async () => {
    if (!crmId) return;
    setActivityLoading(true);
    try {
      const payload = await getCrmConnectorActivity(crmId);
      setActivity(payload);
    } catch {
      setActivity(null);
    } finally {
      setActivityLoading(false);
    }
  }, [crmId]);

  useEffect(() => {
    void loadActivity();
  }, [loadActivity, normalizedStatus.writes_count]);

  const handleDisconnect = async () => {
    if (!crmId || disconnecting) return;
    const label = providerLabel || "this connector";
    if (!window.confirm(`Disconnect ${label}? Existing CRM links on action items stay saved.`)) {
      return;
    }

    setDisconnecting(true);
    setError("");
    try {
      await disconnectCrmConnector(crmId);
      clearGateChoice(crmId);
      const refreshed = normalizeCrmConnectorStatus(
        await getCrmConnectorStatus(crmId).catch(() => ({ connected: false }))
      );
      onStatusChange?.(crmId, refreshed);
    } catch (err) {
      setError(String(err?.message || `Failed to disconnect ${label}`));
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="ds-detail">
      <div className="ds-detail__header">
        <div
          className={`ds-detail__app-icon business-catalog-app-icon ${app?.iconClass || ""}`}
          aria-hidden="true"
        >
          <ConnectSourceCatalogIcon source={app} />
        </div>
        <div className="ds-detail__header-text">
          <h3 className="ds-detail__name">{providerLabel}</h3>
          <span className="ds-detail__header-meta">{headerMeta}</span>
        </div>
        <div className="ds-detail__header-actions">
          <button
            type="button"
            className="ds-detail__remove-icon"
            onClick={() => void handleDisconnect()}
            disabled={disconnecting}
            aria-label={disconnecting ? "Disconnecting connector" : "Disconnect connector"}
            title="Disconnect"
          >
            <IoTrashOutline size={17} aria-hidden="true" />
          </button>
        </div>
      </div>

      {error ? (
        <div className="ds-detail__status ds-detail__status--error" aria-live="polite">
          {error}
        </div>
      ) : null}

      {disconnecting ? (
        <div className="ds-detail__loading">
          <InlineSpinner text="Disconnecting connector…" />
        </div>
      ) : (
        <div className="ds-detail__body">
          <div className="ds-detail__sync-panel">
            <CrmWriteActivityPanel
              providerLabel={providerLabel}
              activity={activity}
              loading={activityLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmConnectorDetailPanel;
